import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import { useSelector } from 'react-redux';
import RoleContainer from '../../../containers/blocks/RoleContainer';
import UserMenuContainer from '../../../containers/menus/UserMenuContainer';
import RequestSupplierReorderModal from '../../modals/RequestSupplierReorderModal';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import useScroll from '../../../hooks/useScroll';
import styles from './SuppliersMenu.module.scss';
import logo from '../../../images/cymbio-logo-only.svg';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { uiProductsFeedsPage } from '../../../constants/featureFlag';

function SuppliersMenu(props) {
  const { supplierId, isAdmin } = props;
  const { pathname } = useLocation();

  const [sticky, setSticky] = useState(false);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);

  useScroll(({ prevPos, currPos }) => {
    if (currPos.y > -56) {
      setSticky(false);
    } else if (currPos.y < prevPos.y) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  });
  const { is_reorder_enabled: reorderPermission, settings } = useSelector((state) => state.role?.supplier) || {};
  const supplierReorderRequest = useSelector((state) => state.supplierReorderRequest.reorder_email_sent_for_suppliers);

  const productsFeedsEnabledForSupplier = !!settings?.products_feeds;
  const productsFeedsPageEnabled = useFeatureFlag(uiProductsFeedsPage, false) && productsFeedsEnabledForSupplier;

  const menuItems = [
    {
      key: 'Catalogue',
      value: [
        { key: 'Catalogue Management', value: '/products/dropship' },
        { key: 'Reorder Catalogue Management', value: '/products/reorder', onClick: (e) => onClickReorderTab(e) },
        { key: 'Pricing', value: '/pricing' },
        {
          key: 'Inventory',
          value: '/inventory',
        },
      ],
    },
    { key: 'sales channels', value: `/retailers` },
    { key: 'orders', value: `/orders` },
    { key: 'stats', value: `/stats` },
  ];

  if (productsFeedsPageEnabled) {
    menuItems[0].value.push({ key: 'Product Feeds', value: '/products/feeds' });
  }

  const mixpanel = useMixpanel();

  const onClickReorderTab = (e) => {
    if (!reorderPermission) {
      e.preventDefault();
      setIsReorderModalOpen(true);
    }
  };

  const reportBi = (event) => {
    reportBusinessActionToMixpanel(mixpanel, event);
  };

  const menuItem = ({ key, value }) => {
    let item = null;

    if (typeof value === 'string') {
      const link = `/suppliers/${supplierId}${value}`;
      const active = pathname.startsWith(link);

      item = (
        <Link className={active ? styles.active : ''} to={link} onClick={() => reportBi(`top-tab-${key}`)}>
          {key}
        </Link>
      );
    } else {
      const subMenuActive = pathname.includes(key);

      item = (
        <div className={styles.subMenuWrapper}>
          <div className={cn(styles.subMenuTitle, subMenuActive ? styles.active : '')}>
            <span>{key}</span>
          </div>
          <div className={cn(styles.subMenu, subMenuActive ? styles.active : '')}>
            {value.map(({ key: subMenuKey, value, onClick }) => {
              return (
                <Link
                  key={subMenuKey}
                  to={`/suppliers/${supplierId}${value}`}
                  onClick={(e) => {
                    if (onClick) onClick(e);
                    reportBi(`top-tab-products-menu-${subMenuKey}}`);
                  }}
                >
                  {subMenuKey}
                </Link>
              );
            })}
          </div>
        </div>
      );
    }

    return <li key={key}>{item}</li>;
  };

  return (
    <div className={cn(styles.wrapper_static, 'menu_wrapper', sticky ? styles.sticky : '')}>
      <header>
        <div className={styles.logo}>
          <Link to="/">
            <img alt="Cymbio Logo" src={logo} />
            <span>{props.supplierName}</span>
          </Link>
        </div>

        <nav>
          <ul className={styles.menuItems}>{menuItems.map((item) => menuItem(item))}</ul>
        </nav>

        <div className={styles.panel}>
          <div className={styles.panelIcons}>
            {isAdmin ? (
              <Link to={`/suppliers/${supplierId}/admin`} className={styles.linkSettings}>
                <span className={cn('ic-settings', styles['ic-settings'])} />
              </Link>
            ) : (
              ''
            )}

            <RoleContainer suppliersMenu />

            <UserMenuContainer suppliersMenu />

            <span onClick={() => setSticky(!sticky)} className={cn('ic-menu', styles['ic-menu'])} />

            <span onClick={() => setSticky(!sticky)} className={cn('ic-close', styles['ic-close'])} />
          </div>
        </div>
      </header>
      <RequestSupplierReorderModal
        isOpen={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        wasEmailSent={supplierReorderRequest.find((emailSupplierId) => emailSupplierId === supplierId)}
      />
    </div>
  );
}

export default SuppliersMenu;

SuppliersMenu.defaultProps = {
  supplierName: '',
  isAdmin: false,
};

SuppliersMenu.propTypes = {
  supplierId: PropTypes.number,
  supplierName: PropTypes.string,
  isAdmin: PropTypes.bool,
};
